import { fetchAuthSession } from 'aws-amplify/auth';
import { downloadData, list, remove } from 'aws-amplify/storage';
import getStorageFileDirectory from '../common-components/upload-entity-documents-modal/upload-entity-documents.utils';
import {
  type DocumentWithMeta,
  type EntityDocument,
  EntityType,
  FileOwner,
  FileOwnerToStorageLevel,
  IDocument,
  S3ListOutputItem,
  StorageLevel,
  serializeS3ProviderListOutputItem,
} from '../models/documents/document';
import store from '../store';
import type { UserFeatureFlags } from '../user/user.slice';
import DateTimeHelpers from '../utils/date-time-helpers.utils';
import DownloadHelpers from '../utils/download.utils';
import { getFilenameFromDirectory } from '../utils/text-formatting.utils';
import { wrapRequest } from './base';

interface GetEntityDocumentBody extends Pick<EntityDocument, 'entity_id'> {
  company_id: string;
  user_id: string;
  tenant_id: string;
}

interface PutDocumentMetaDataParams {
  file: File;
  fileOwner: FileOwner;
  storageLevel: StorageLevel;
  entityId: string;
  entityType: string;
  description: string;
  name: string;
  s3_location: string;
}

const getStorageLevel = (fileOwner: FileOwner): StorageLevel =>
  FileOwnerToStorageLevel[fileOwner];

export const putDocumentMetadata = async ({
  file,
  fileOwner,
  storageLevel,
  entityId,
  entityType,
  description,
  name,
  s3_location,
}: PutDocumentMetaDataParams): Promise<'Successfully updated document information'> => {
  const tenantId = store.getState().user.idToken?.tenantId;
  const companyId = store.getState().user.idToken?.companyId;
  const userId = store.getState().user.idToken?.sub;

  return wrapRequest('put', 'geonius', '/documents', {
    body: {
      filename: name || file.name,
      date_uploaded: DateTimeHelpers.dateToIso8601(new Date()),
      file_date: DateTimeHelpers.dateToIso8601(new Date(file.lastModified)),
      description,
      storage_level: storageLevel,
      entity_id: entityId,
      entity_type: entityType,
      file_tenant: tenantId,
      file_company: companyId,
      file_owner: userId,
      file_owner_enum: fileOwner,
      file_size: file.size,
      content_type: file.type,
      s3_location,
    },
  });
};

const getDocumentMetadata = async (
  body: Partial<GetEntityDocumentBody> = {
    company_id: store.getState().user.idToken?.companyId,
    user_id: store.getState().user.idToken?.sub,
    tenant_id: store.getState().user.idToken?.tenantId,
  }
): Promise<EntityDocument[]> =>
  wrapRequest('get', 'geonius', `/documents`, {
    queryStringParameters: {
      ...body,
    },
  });

const deleteDocumentMetadata = async (
  storageLevel: StorageLevel,
  documentId: string,
  entityId: string,
  entityType: string
): Promise<'Successfully deleted document metadata'> => {
  const tenantId = store.getState().user.idToken?.tenantId;
  const companyId = store.getState().user.idToken?.companyId;
  const userId = store.getState().user.idToken?.sub;
  return wrapRequest('del', 'geonius', `/documents`, {
    body: {
      data: {
        document_id: documentId,
        entity_id: entityId,
        entity_type: entityType,
        company_id: companyId,
        user_id: userId,
        tenant_id: tenantId,
        file_owner_enum:
          storageLevel === StorageLevel.PRIVATE
            ? FileOwner.PRIVATE
            : storageLevel,
      },
    },
  });
};

// These functions are technically not API calls,
// but we never use the S3 objects without enriching them with metadata from the api, so they can live here

const addMetadataToS3Objects = async (
  s3Objects: IDocument[],
  metadataPromise: Promise<EntityDocument[]>
): Promise<DocumentWithMeta[]> => {
  const metadata = await metadataPromise;
  return s3Objects
    .map((s3Object) => {
      const metaDoc = metadata.find((m) => m.s3_location === s3Object.key);
      if (metaDoc) {
        return {
          ...s3Object,
          metadata: metaDoc,
        } as DocumentWithMeta;
      }
      // Can't handle documents without metadata
      return null;
    })
    .filter(Boolean) as DocumentWithMeta[];
};

const serializeResults = (results: S3ListOutputItem[]): IDocument[] =>
  results.map((result) => serializeS3ProviderListOutputItem(result));

/**
 * Get S3 Docs from Amplify Storage, and add metadata from the API
 * Supports passing through filters to getDocumentMetadata
 * @param body
 * @returns
 */

function getDocsPromise(
  storageLevel: StorageLevel,
  metadataPromise: Promise<any>,
  body?: Partial<GetEntityDocumentBody>
) {
  return (async () => {
    const directory = getStorageFileDirectory(storageLevel, body?.entity_id);
    let finalPath = directory;

    if (storageLevel === StorageLevel.PRIVATE) {
      const session = await fetchAuthSession();
      const { identityId } = session;
      finalPath = `private/${identityId}/${directory}`;
    }

    const { items } = await list({
      path: finalPath,
    });

    const serialized = serializeResults(items);
    return addMetadataToS3Objects(serialized, metadataPromise);
  })();
}

const EntityTypeToFeatureFlag: Partial<
  Record<EntityType, keyof UserFeatureFlags>
> = {
  [EntityType.port]: 'ports_license' as keyof UserFeatureFlags,
};

const filterDocumentsOnUsersLicense = (
  documents: DocumentWithMeta[],
  licenses: UserFeatureFlags | null
) =>
  documents.filter(({ metadata }) => {
    const license = EntityTypeToFeatureFlag[metadata.entity_type as EntityType];
    return license ? licenses?.[license] : true;
  });

export const getS3DocsWithMetadata = async (
  body?: Partial<GetEntityDocumentBody>
): Promise<{
  myDocuments: DocumentWithMeta[];
  organisationDocuments: DocumentWithMeta[];
  tenantDocuments: DocumentWithMeta[];
}> => {
  try {
    const tenantId = store.getState().user.idToken?.tenantId;
    const myDocumentsMetaPromise = getDocumentMetadata(body);
    const licenses = store.getState().user.featureFlags ?? null;

    const myDocsPromise = getDocsPromise(
      StorageLevel.PRIVATE,
      myDocumentsMetaPromise,
      body
    );
    const orgDocsPromise = getDocsPromise(
      StorageLevel.COMPANY,
      myDocumentsMetaPromise,
      body
    );
    const tenantDocsPromise = getDocsPromise(
      StorageLevel.TENANT,
      myDocumentsMetaPromise,
      body
    );

    const [myDocuments, organisationDocuments, tenantDocuments] =
      await Promise.all([myDocsPromise, orgDocsPromise, tenantDocsPromise]);
    return {
      myDocuments,
      organisationDocuments: filterDocumentsOnUsersLicense(
        organisationDocuments,
        licenses
      ).filter((doc) => doc.metadata.file_owner_enum === FileOwner.COMPANY),
      tenantDocuments: filterDocumentsOnUsersLicense(
        tenantDocuments,
        licenses
      ).filter(
        (doc) =>
          doc.metadata.file_owner_enum === FileOwner.TENANT &&
          doc.metadata.file_tenant === tenantId
      ),
    };
  } catch {
    return {
      myDocuments: [],
      organisationDocuments: [],
      tenantDocuments: [],
    };
  }
};

export const downloadDocument = async (document: IDocument) => {
  const { key, metadata } = document;

  if (!metadata) {
    return Promise.reject(new Error('No metadata found for document'));
  }

  const storageLevel = getStorageLevel(metadata.file_owner_enum);
  if (!storageLevel) {
    return Promise.reject(new Error('Invalid storage level'));
  }

  try {
    const downloadResult = await downloadData({
      path: key!,
    }).result;

    const blob = await downloadResult.body.blob();
    const uri = URL.createObjectURL(blob);

    return DownloadHelpers.downloadURI(
      getFilenameFromDirectory(key!) as string,
      uri
    );
  } catch {
    return Promise.reject(new Error('Failed to download document'));
  }
};

export const deleteDocument = async (document: DocumentWithMeta) => {
  const storageLevel = getStorageLevel(document.metadata.file_owner_enum);

  const getDocumentDeletePromise = async () =>
    remove({
      path: document.key!,
    });

  const getDocumentMetadataDeletePromises = () =>
    deleteDocumentMetadata(
      storageLevel,
      document.metadata.document_id!,
      document.metadata.entity_id!,
      document.metadata.entity_type!
    );

  return Promise.all([
    getDocumentDeletePromise(),
    getDocumentMetadataDeletePromises(),
  ]);
};
